import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import {
  getClaimSpecificFields,
  mapDiariesToTableRows,
} from '../../../functions/todo/diary-mapper';
import { SearchDiariesRequest } from '../../../models/api/todo/client-diaries/search-diaries-request';
import {
  clearDiarySearchUpdates,
  rememberDiarySearchUpdate,
} from '../../../functions/todo/search-retain-updates';
import { FormatPipe } from '../../../pipes/format.pipe';
import { ClientDiaryDataService } from '../../../services/todo/client-diary-data.service';
import { DiaryCompleteService } from '../../../services/todo/diary-complete.service';
import { BaseComponent } from '../../base/base.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { PanelComponent } from '../../core/panels/panel.component';
import {
  DIARY_SEARCH_STATE_CLAIM,
  DIARY_SEARCH_STATE_MAIN,
  DiarySearchSessionState,
} from './diary-storage.keys';
import { DiarySearchComponent } from './diary-search.component';
import { DiaryTableTopComponent } from './diary-table-top.component';
import { SharedDiaryPrefDialogComponent } from './shared-diary-pref-dialog.component';

interface DiarySearchMatchRow {
  status?: string;
  dateDue?: Date | string;
  typeUID?: string | number;
  followUpUser?: string;
  followUpUserId?: string | number;
  clientNumber?: number | string | null;
}

@Component({
  selector: 'app-diary-search-panel',
  imports: [
    DiarySearchComponent,
    DiaryTableTopComponent,
    PanelComponent,
    EmptyStateComponent,
    SharedDiaryPrefDialogComponent,
  ],
  providers: [DatePipe, FormatPipe],
  template: `
    <app-diary-search
      [isPageTodo]="isPageTodo"
      [loading]="tableLoading"
      (searchRequest)="onSearchRequest($event)"
      (clear)="onClearSearch()"
    />

    <app-diary-table-top
      [showActionButtons]="true"
      [showTable]="false"
      [showPreferencesGear]="true"
      [isPageTodo]="isPageTodo"
      (preferencesClick)="openPreferences()"
    />

    @if (!searchPerformed) {
      <app-empty-state
        icon="fa-light fa-magnifying-glass"
        label="Search results"
        message="Search results will appear here"
      />
    } @else if (tableRows.length === 0 && !tableLoading) {
      <app-empty-state icon="fa-table fa-light" label="No diaries" />
    } @else {
      <app-panel>
        <app-diary-table-top
          [diaryEntries]="tableRows"
          [isPageTodo]="isPageTodo"
          [loading]="tableLoading"
          [error]="error"
          [showSelection]="true"
          (markCompleteEvent)="onMarkComplete($event)"
          (markSelectedCompleteEvent)="onMarkComplete($event)"
          (markAllCompleteEvent)="onMarkComplete($event)"
        />
      </app-panel>
    }

    <app-shared-diary-pref-dialog #prefDialog />
  `,
  styles: ``,
})
export class DiarySearchPanelComponent extends BaseComponent implements OnInit {
  private formatPipe = inject(FormatPipe);
  private diaryComplete = inject(DiaryCompleteService);
  private clientDiaryData = inject(ClientDiaryDataService);

  @ViewChild('prefDialog') prefDialog?: SharedDiaryPrefDialogComponent;

  isPageTodo = false;
  claim: any = null;
  tableLoading = false;
  error: any = null;
  searchPerformed = false;
  tableRows: any[] = [];
  private diaryRequest: SearchDiariesRequest;
  private rawSearchResults: any[] = [];
  private searchGeneration = 0;
  private searchPagePath = '';

  constructor() {
    super();
    let previousUrl = this.router.url ?? '';
    this.dispose.subs = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nextUrl = event.urlAfterRedirects ?? event.url;
        const fromEdit = previousUrl.startsWith('/diary-edit');
        if (fromEdit && this.searchPerformed) {
          this.syncResultsAfterLocalUpdate();
        }
        previousUrl = nextUrl;
      });
    this.dispose.subs = this.clientDiaryData.diariesChanged.subscribe(() => {
      if (this.searchPerformed) {
        this.syncResultsAfterLocalUpdate();
      }
    });
  }

  ngOnInit(): void {
    this.searchPagePath = document.location.pathname;
    this.isPageTodo = this.searchPagePath === '/todo';
    this.claim = this.view.currentClaim();
    this.restoreSessionState();
    this.applySearchPatch();
  }

  openPreferences(): void {
    void this.prefDialog?.show();
  }

  private get sessionKey(): string {
    return this.isPageTodo
      ? DIARY_SEARCH_STATE_MAIN
      : DIARY_SEARCH_STATE_CLAIM;
  }

  private restoreSessionState(): void {
    const saved = this.storage.get(this.sessionKey) as
      | DiarySearchSessionState
      | undefined;
    if (saved?.performed && Array.isArray(saved.results)) {
      this.searchPerformed = true;
      this.rawSearchResults = saved.results as any[];
      this.diaryRequest = saved.request as SearchDiariesRequest;
      this.applyTableRows();
    }
  }

  private persistSessionState(): void {
    this.storage.set(this.sessionKey, {
      request: this.diaryRequest,
      results: this.rawSearchResults,
      performed: this.searchPerformed,
    });
  }

  async onSearchRequest(request: SearchDiariesRequest): Promise<void> {
    const generation = ++this.searchGeneration;
    this.tableLoading = true;
    this.error = null;
    this.view.showLoading();
    try {
      this.searchPerformed = true;
      const diaryRequest = request;
      if (!this.isPageTodo && this.claim?.recordId != null) {
        Object.assign(diaryRequest, getClaimSpecificFields(this.claim));
      }
      const results = await this.api.clientDiary.getDiaryEntries(diaryRequest);
      if (generation !== this.searchGeneration) {
        return;
      }
      this.diaryRequest = diaryRequest;
      this.rawSearchResults = results;
      clearDiarySearchUpdates(this.storage);
      this.persistSessionState();
      this.applyTableRows();
    } catch (error) {
      if (generation === this.searchGeneration) {
        this.error = error;
      }
      throw error;
    } finally {
      if (generation === this.searchGeneration) {
        this.tableLoading = false;
        this.view.hideLoading();
      }
    }
  }

  onClearSearch(): void {
    clearDiarySearchUpdates(this.storage);
    this.searchPerformed = false;
    this.rawSearchResults = [];
    this.tableRows = [];
    this.storage.remove(this.sessionKey);
  }

  private applyTableRows(): void {
    this.tableRows = mapDiariesToTableRows(this.rawSearchResults, this.formatPipe, {
      isPageTodo: this.isPageTodo,
      claim: this.claim,
      includeCompleted: true,
      isSearchView: true,
    });
  }

  onMarkComplete(ids: number | number[]): void {
    const diaryIds = Array.isArray(ids) ? ids : [ids];
    this.diaryComplete.confirmAndMarkComplete(diaryIds, () =>
      this.removeCompletedFromResults(diaryIds),
    );
  }

  private removeCompletedFromResults(ids: number[]): void {
    const idSet = new Set(ids);
    this.rawSearchResults = this.rawSearchResults.filter((diary): boolean => {
      if (!idSet.has(diary.diaryUID)) {
        return true;
      }
      const saved: DiarySearchMatchRow = { ...diary, status: 'Completed' };
      rememberDiarySearchUpdate(this.storage, {
        id: diary.diaryUID,
        isCompleted: true,
      });
      const reason = this.diaryMismatchReason(saved);
      return reason == null;
    });
    this.persistSessionState();
    this.applyTableRows();
  }

  /** Drop the edited row when it no longer matches. Leave a matching row unchanged. */
  private syncResultsAfterLocalUpdate(): void {
    this.applySearchPatch();
  }

  private searchPatchKey(): string {
    const page = this.searchPagePath || document.location.pathname;
    return `client-diary-search-patch:${page}`;
  }

  private applySearchPatch(): void {
    const key = this.searchPatchKey();
    const patch = this.storage.get(key);
    if (!patch) {
      return;
    }
    this.storage.remove(key);
    if (!this.searchPerformed || patch.id == null) {
      return;
    }
    const idx = this.rawSearchResults.findIndex(
      (diary): boolean => diary.diaryUID === patch.id,
    );
    if (idx >= 0) {
      const existing = this.rawSearchResults[idx];
      const candidate: DiarySearchMatchRow = {
        status: patch.isCompleted
          ? 'Completed'
          : existing.status === 'Completed'
            ? 'Open'
            : existing.status,
        dateDue: patch.dueDate ?? existing.dateDue,
        typeUID: patch.typeUID ?? existing.typeUID,
        followUpUser: patch.followUpUser || existing.followUpUser,
        followUpUserId: patch.followUpUserId || existing.followUpUserId,
        clientNumber: patch.clientNumber ?? existing.clientNumber,
      };
      const reason = this.diaryMismatchReason(candidate);
      this.rawSearchResults = reason
        ? this.rawSearchResults.filter(
            (diary): boolean => diary.diaryUID !== patch.id,
          )
        : this.rawSearchResults;
    }
    this.persistSessionState();
    this.applyTableRows();
  }

  private diaryMismatchReason(diary: DiarySearchMatchRow): string | null {
    const request = this.diaryRequest;
    if (!request) {
      return null;
    }
    const statusReason = this.diaryStatusReason(diary, request);
    if (statusReason) {
      return statusReason;
    }
    if (
      request.typeUID != null &&
      String(diary.typeUID) !== String(request.typeUID)
    ) {
      return 'type';
    }
    if (request.followUpBy) {
      const selectedId = String(request.followUpBy);
      const rowId =
        diary.followUpUserId != null ? String(diary.followUpUserId) : '';
      const display = String(diary.followUpUser ?? '');
      if (rowId !== selectedId && !display.includes(`(${selectedId})`)) {
        return 'follow-up';
      }
    }
    if (request.clientNumber != null && diary.clientNumber != null) {
      const rowClient = Number(diary.clientNumber);
      if (
        Number.isFinite(rowClient) &&
        rowClient !== Number(request.clientNumber)
      ) {
        return 'client';
      }
    }
    if (request.dueDateStart || request.dueDateEnd) {
      const due = this.startOfDay(diary.dateDue);
      if (!due) {
        return 'due-date';
      }
      const start = this.startOfDay(request.dueDateStart);
      const end = this.startOfDay(request.dueDateEnd);
      if (start && due < start) {
        return 'due-before-start';
      }
      if (end && end < due) {
        return 'due-after-end';
      }
    }
    return null;
  }

  private diaryStatusReason(
    diary: DiarySearchMatchRow,
    request: SearchDiariesRequest,
  ): string | null {
    const status = (diary.status || '').toLowerCase();
    if (request.diaryStatus === 1 && status === 'completed') {
      return 'status:Completed';
    }
    if (request.diaryStatus === 2 && status !== 'completed') {
      return `status:${diary.status || 'Open'}`;
    }
    const duePast = !!request.duePast;
    const dueToday = !!request.dueToday;
    const dueFuture = !!request.dueFuture;
    if (!duePast && !dueToday && !dueFuture) {
      return null;
    }
    if (!diary.dateDue) {
      return null;
    }
    const due = this.startOfDay(diary.dateDue);
    if (!due) {
      return 'due-bucket';
    }
    const today = this.startOfDay(new Date());
    const isPast = due < today;
    const isToday = due.getTime() === today.getTime();
    const isFuture = due > today;
    const matches =
      (duePast && isPast) || (dueToday && isToday) || (dueFuture && isFuture);
    return matches ? null : 'due-bucket';
  }

  private startOfDay(value: Date | string | null | undefined): Date {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? new Date(value) : new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }
    date.setHours(0, 0, 0, 0);
    return date;
  }

}
